import { useState, useCallback } from 'react';
import { AlertCircle, FileDown, Sparkles } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ModuleAnswerReviewContent } from '@/components/results/ModuleAnswerReviewContent';
import { ExportPanel } from './ExportPanel';
import { ModulePrintView } from './ModulePrintView';
import { ReanalyseModuleDialog } from './ReanalyseModuleDialog';
import { useModuleExport } from '@/hooks/useModuleExport';
import { downloadAnswerHtmlArchive } from '@/lib/answerArchiveApi';
import type { ModuleAnswer, ModuleResult } from '@/types/simulation-results';
import type { UserSubmission } from './types';

const REANALYSABLE_MODULES = new Set(['writing', 'speaking']);

interface ModulePreviewDialogProps {
  readonly submission: UserSubmission | null;
  readonly moduleName: string;
  readonly answers: ModuleAnswer[];
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly sessionModules: ModuleResult[];
  readonly onModuleChange: (module: string) => void;
  readonly onClose: () => void;
  readonly onRefreshAnswers?: () => Promise<void> | void;
}

const MODULE_LABELS: Record<string, string> = {
  reading: 'Reading',
  listening: 'Listening',
  writing: 'Writing',
  speaking: 'Speaking',
};

export function ModulePreviewDialog({
  submission,
  moduleName,
  answers,
  isLoading,
  error,
  sessionModules,
  onModuleChange,
  onClose,
  onRefreshAnswers,
}: Readonly<ModulePreviewDialogProps>) {
  const moduleLabel = MODULE_LABELS[moduleName] ?? moduleName;
  const [exportPanelOpen, setExportPanelOpen] = useState(false);
  const [pendingModules, setPendingModules] = useState<string[] | null>(null);
  const [reanalyseOpen, setReanalyseOpen] = useState(false);
  const [isHtmlExporting, setIsHtmlExporting] = useState(false);
  const canReanalyse =
    !!submission && REANALYSABLE_MODULES.has(moduleName) && answers.length > 0;

  const { isExporting, answersMap, fetchModulesForExport, clearExport } = useModuleExport();

  const handleCloseExport = useCallback(() => {
    setExportPanelOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setExportPanelOpen(false);
    clearExport();
    setPendingModules(null);
    onClose();
  }, [clearExport, onClose]);

  const handleExport = useCallback(
    async (selectedModules: string[]) => {
      if (!submission) return;
      setPendingModules(selectedModules);
      await fetchModulesForExport(submission.session_id, selectedModules);
    },
    [submission, fetchModulesForExport],
  );

  const handleExportJson = useCallback(
    async (selectedModules: string[]) => {
      if (!submission) return;
      const map = await fetchModulesForExport(submission.session_id, selectedModules);
      if (!map) return;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const blob = new Blob(
        [JSON.stringify({
          sessionId: submission.session_id,
          username: submission.username,
          exportedAt: new Date().toISOString(),
          modules: map,
        }, null, 2)],
        { type: 'application/json' },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `answers_${submission.session_id}_${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      clearExport();
    },
    [submission, fetchModulesForExport, clearExport],
  );

  const handleExportHtml = useCallback(
    async (selectedModules: string[], audience: 'full' | 'examiner' = 'full') => {
      if (!submission) return;
      setIsHtmlExporting(true);
      try {
        await downloadAnswerHtmlArchive({
          sessionId: submission.session_id,
          modules: selectedModules,
          source: 'current',
          audience,
          filenameHint: submission.username,
          metadata: {
            username: submission.username,
            examType: submission.exam_type,
            level: submission.level,
          },
        });
      } finally {
        setIsHtmlExporting(false);
      }
    },
    [submission],
  );

  const handlePrintDone = useCallback(() => {
    setPendingModules(null);
    clearExport();
    setExportPanelOpen(false);
  }, [clearExport]);

  const availableModules = sessionModules.filter((m) => m.is_graded);

  return (
    <>
      <Dialog
        open={!!submission}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
      >
        <DialogContent className="flex h-[90vh] max-h-[90vh] w-[96vw] max-w-[96rem] flex-col overflow-hidden p-0">
          <DialogHeader className="border-b border-border px-5 py-4 sm:px-6">
            <div className="flex items-start justify-between pr-8">
              <div className="flex-1">
                <DialogTitle className="font-ui">
                  {submission
                    ? `${submission.username}'s ${moduleLabel} Preview`
                    : 'Submission Preview'}
                </DialogTitle>
                <DialogDescription className="font-ui">
                  Read-only learner results view for this module.
                </DialogDescription>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {canReanalyse && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 text-xs"
                    onClick={() => setReanalyseOpen(true)}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Re-analyse
                  </Button>
                )}
                {availableModules.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 text-xs"
                    onClick={() => setExportPanelOpen((prev) => !prev)}
                  >
                    <FileDown className="h-3.5 w-3.5" />
                    Extract
                  </Button>
                )}
              </div>
            </div>

            {exportPanelOpen && availableModules.length > 0 && (
              <ExportPanel
                availableModules={availableModules}
                isExporting={isExporting}
                isHtmlExporting={isHtmlExporting}
                onExport={handleExport}
                onExportJson={handleExportJson}
                onExportHtml={handleExportHtml}
              />
            )}
          </DialogHeader>

          <div className="flex min-h-0 flex-1 flex-col">
            {error ? (
              <Card className="m-6 border-destructive/30 bg-destructive/5">
                <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
                  <AlertCircle className="h-10 w-10 text-destructive" />
                  <p className="font-ui text-sm text-foreground">{error}</p>
                </CardContent>
              </Card>
            ) : (
              <ModuleAnswerReviewContent
                layout="dialog"
                sessionId={submission?.session_id}
                moduleName={moduleName}
                answers={answers}
                isLoading={isLoading}
                sessionModules={sessionModules}
                onSessionModuleChange={onModuleChange}
                itemRatingsMode="learner"
                className="min-h-0 flex-1"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {answersMap && pendingModules && submission && (
        <ModulePrintView
          submission={submission}
          modules={pendingModules}
          answersMap={answersMap}
          sessionModules={sessionModules}
          onPrintDone={handlePrintDone}
        />
      )}

      {submission && (
        <ReanalyseModuleDialog
          open={reanalyseOpen}
          sessionId={submission.session_id}
          moduleName={moduleName}
          answers={answers}
          onClose={() => setReanalyseOpen(false)}
          onSaved={async () => {
            if (onRefreshAnswers) await onRefreshAnswers();
          }}
        />
      )}
    </>
  );
}
